<script setup lang="ts">
/*
 *
 * IMPORTS
 *
 * */

// PRIMEVUE
import PMessage from 'primevue/message'

// VUE, VUE-ROUTER, etc.
import { computed, onMounted, ref, type Ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'

//Custom components and types
import CLearningModuleGrain from '@/presentation/components/learning/module/CLearningModuleGrain.vue'
import CLearningModuleHeader from '@/presentation/components/learning/module/CLearningModuleHeader.vue'
import CLearningModuleTasks from '@/presentation/components/learning/module/CLearningModuleTasks.vue'
import CLearningModuleListGrains from '@/presentation/components/learning/module/CLearningModuleListGrains.vue'

// STORES
import { useLearningModulesStore } from '@/presentation/stores/learning/learningModules.ts'
import { LearningModuleInput } from '@/core/domain/learning/module/LearningModule.ts'
import { LearningStatus } from '@/core/domain/resources/enums/LearningStatus.ts'
import type { LearningGrain } from '@/core/domain/learning/grain/LearningGrain.ts'
import { useLearningCoursesStore } from '@/presentation/stores/learning/learningCourses.ts'
import { storeToRefs } from 'pinia'
import { storeEventBus } from '@/presentation/stores/utils/storeEventBus.ts'
import type { Quiz } from '@/core/domain/quiz/Quiz.ts'

// UTILS
const route = useRoute()
const router = useRouter()

const learningModulesStore = useLearningModulesStore()
const learningCoursesStore = useLearningCoursesStore()

// COMPUTED

// detect if we are creating a module
const isCreatingModule = computed(() => {
  return route.name === 'learning-course-add-module'
})

// Détecte s'il y a des grains non sauvegardés (id === 0)
const hasUnsavedGrains = computed(() => {
  if (!learningModule.value?.learningGrains) return false
  return learningModule.value.learningGrains.some((grain) => grain.id === 0)
})

const unsavedGrainsCount = computed(() => {
  if (!learningModule.value?.learningGrains) return 0
  return learningModule.value.learningGrains.filter((grain) => grain.id === 0).length
})

// PROPS
const props = defineProps<{
  courseId: number
  moduleId?: number
}>()

// DATA
const { currentCourse } = storeToRefs(learningCoursesStore)
const loading = ref(true)
const fieldToUpdate: Ref<string | null> = ref(null)
const learningModule: Ref<LearningModuleInput | null> = ref(null)
const currentDisplayedGrain: Ref<LearningGrain | null> = ref(null)

storeEventBus.useEventListener('learning:grain:deleted', (payload) => {
  if (learningModule.value && learningModule.value.learningGrains) {
    learningModule.value.learningGrains = learningModule.value.learningGrains.filter(
      (grain) => grain.id !== payload.grainId
    )
  }
})

storeEventBus.useEventListener('learning:quizgrain:updated', (payload) => {
  updateGrainQuiz(payload.quiz)
})

const findGrainWithQuiz = (quiz: Quiz) => {
  if (!learningModule.value?.learningGrains) return undefined

  return learningModule.value.learningGrains.find(
    (g) => g.quizPre?.id === quiz.id || g.quizPost?.id === quiz.id
  )
}

const updateGrainQuiz = (quiz: Quiz) => {
  const grain = findGrainWithQuiz(quiz)
  if (grain) {
    if (grain.quizPre?.id === quiz.id) {
      grain.quizPre = quiz
    } else if (grain.quizPost?.id === quiz.id) {
      grain.quizPost = quiz
    }
  }
}

const cancelUpdateGrain = () => {
  if (currentDisplayedGrain.value) {
    currentDisplayedGrain.value.deleteAllSupportDrafts()

    // Si le grain n'a jamais été enregistré (id === 0), le supprimer de la liste
    if (currentDisplayedGrain.value.id === 0 && learningModule.value) {
      learningModule.value.removeGrain(currentDisplayedGrain.value)
      currentDisplayedGrain.value = null
    }
  }

  fieldToUpdate.value = null
}

// HAPPENS ON MOUNTED
onMounted(async () => {
  if (currentCourse.value) {
    if (isCreatingModule.value) {
      learningModule.value = new LearningModuleInput({
        id: 0,
        order: currentCourse.value.modulesCount() + 1,
        title: isCreatingModule.value ? 'Nouveau module' : '',
        status: LearningStatus.Values.DRAFT
      })

      fieldToUpdate.value = 'title'
    }

    if (props.moduleId) {
      const editedModule = await learningModulesStore.fetchModuleById(
        props.moduleId,
        currentCourse.value.id,
        {
          withGrains: true,
          withTask: true,
          withSupports: false,
          status: [LearningStatus.Values.DRAFT, LearningStatus.Values.ACTIVE]
        }
      )
      if (editedModule) {
        learningModule.value = editedModule.createLearningModuleInput()
      }

      if (route.query.grainId) {
        const grainId = Number(route.query.grainId)
        const grain = editedModule.learningGrains.find((g) => g.id === grainId)
        if (grain) {
          currentDisplayedGrain.value = grain
        }
      }
    }
  }

  loading.value = false
})

function cancelUpdate() {
  if (isCreatingModule)
    router.push({
      name: 'learning-course-update',
      params: { courseId: props.courseId }
    })
}
</script>

<template>
  <template v-if="currentCourse">
    <c-learning-module-header
      @cancel-update="cancelUpdate"
      v-model:learning-module="learningModule"
      v-model:field-to-update="fieldToUpdate"
      :learning-course-title="currentCourse.title"
      :is-creating="isCreatingModule"
      :learning-course-id="currentCourse.id"
    />

    <div class="col-span-12 flex-1 gap-8 overflow-hidden">
      <div class="grid h-full grid-cols-12">
        <!--  Left side    -->
        <div
          class="border-surface surface-b col-span-4 flex flex-col gap-4 overflow-auto border-r p-8"
        >
          <c-learning-module-tasks
            :field-to-update="fieldToUpdate"
            :domain="currentCourse.domain"
            :learning-course-id="currentCourse.id"
            v-model:module="learningModule"
            :is-creating="isCreatingModule"
          />

          <p-message severity="info" icon="pi pi-info-circle" v-if="isCreatingModule">
            <p>
              Pour créer un module, veuillez définir un titre et sélectionner une tâche du
              référentiel.
            </p>
          </p-message>

          <c-learning-module-list-grains
            v-if="learningModule"
            v-model:current-displayed-grain="currentDisplayedGrain"
            v-model:field-to-update="fieldToUpdate"
            :is-creating-module="isCreatingModule"
            :learning-module="learningModule"
            :has-unsaved-grains="hasUnsavedGrains"
          />
        </div>

        <!--  Right side   -->
        <div v-if="learningModule" class="col-span-8 overflow-auto bg-white p-8">
          <!-- Message d'avertissement pour les grains non sauvegardés -->
          <p-message icon="pi pi-exclamation-triangle" severity="warn" v-if="hasUnsavedGrains" class="mb-4">
            <p>
              Attention : {{ unsavedGrainsCount }} grain{{ unsavedGrainsCount > 1 ? 's' : '' }} non sauvegardé{{ unsavedGrainsCount > 1 ? 's' : '' }}.
              Veuillez valider ou annuler {{ unsavedGrainsCount > 1 ? 'ces grains' : 'ce grain' }} avant de continuer.
            </p>
          </p-message>

          <p-message icon="pi pi-info-circle" severity="info" v-if="!learningModule.hasGrains">
            <p>Veuillez créer un grain pour l'afficher.</p>
          </p-message>
          <p-message icon="pi pi-info-circle" severity="info" v-else-if="!currentDisplayedGrain">
            Sélectionnez un grain dans la liste pour l'afficher.
          </p-message>
          <c-learning-module-grain
            v-else
            v-model:field-to-update="fieldToUpdate"
            v-model:learning-grain="currentDisplayedGrain"
            v-if="currentDisplayedGrain"
            v-model:learning-module="learningModule"
            :learning-course-id="courseId"
            @cancel="cancelUpdateGrain"
            @delete="() => (currentDisplayedGrain = null)"
          />
        </div>
      </div>
    </div>
  </template>
</template>
